export const CustomerType = {
  FISICA: 'fisica',
  JURIDICA: 'juridica'
} as const;

export type CustomerTypeValue = typeof CustomerType[keyof typeof CustomerType];

export class Customer {
  id: number;
  companyId: string;
  cif: string;
  type: CustomerTypeValue;
  name?: string;
  firstSurname?: string;
  secondSurname?: string;
  businessName?: string;
  tradeName?: string;
  address?: string;
  city?: string;
  province?: string;
  country?: string;
  postCode?: string;
  email?: string;
  phone?: string;
  website?: string;
  bankAccountNumber?: string;
  bic?: string;
  // FACe fields
  DIRe?: string;
  accountingOffice?: string;
  managementBody?: string;
  processingUnit?: string;
  proposingBody?: string;
  mandateId?: string;
  mandateSignatureDate?: string;
  debitAccount?: string;
  paymentMeans?: string;
  paymentInformation?: string;
  pdfLanguage?: string;
  timestampModified?: string;

  constructor(data: Partial<Customer>) {
    Object.assign(this, data);
  }

  static fromDTO(dto: any, companyId: string): Customer {
    return new Customer({
      id: dto.id,
      companyId: dto.company_id || companyId,
      cif: dto.cif || '',
      type: dto.type || CustomerType.JURIDICA,
      name: dto.name,
      firstSurname: dto.first_surname,
      secondSurname: dto.second_surname,
      businessName: dto.business_name,
      tradeName: dto.trade_name || dto.alias,
      address: dto.address,
      city: dto.city,
      province: dto.province,
      country: dto.country,
      postCode: dto.post_code || dto.zip_code,
      email: dto.email,
      phone: dto.phone,
      website: dto.website,
      bankAccountNumber: dto.bank_account_number,
      bic: dto.bic,
      // FACe fields
      DIRe: dto.DIRe,
      accountingOffice: dto.accounting_office,
      managementBody: dto.management_body,
      processingUnit: dto.processing_unit,
      proposingBody: dto.proposing_body,
      mandateId: dto.mandate_id,
      mandateSignatureDate: dto.mandate_signature_date,
      debitAccount: dto.debit_account,
      paymentMeans: dto.payment_means,
      paymentInformation: dto.payment_information,
      pdfLanguage: dto.pdf_language,
      timestampModified: dto.timestamp_modified
    });
  }

  toDTO(): any {
    return {
      name: this.name,
      first_surname: this.firstSurname,
      second_surname: this.secondSurname,
      business_name: this.businessName,
      alias: this.tradeName,
      cif: this.cif,
      address: this.address,
      city: this.city,
      province: this.province,
      country: this.country,
      zip_code: this.postCode,
      email: this.email,
      phone: this.phone,
      website: this.website,
      bank_account_number: this.bankAccountNumber,
      bic: this.bic,
      type: this.type,
      companydb: this.companyId,
      // FACe fields
      DIRe: this.DIRe,
      accounting_office: this.accountingOffice,
      management_body: this.managementBody,
      processing_unit: this.processingUnit,
      proposing_body: this.proposingBody,
      mandate_id: this.mandateId,
      mandate_signature_date: this.mandateSignatureDate,
      debit_account: this.debitAccount,
      payment_means: this.paymentMeans,
      payment_information: this.paymentInformation,
      pdf_language: this.pdfLanguage,
      timestamp_modified: this.timestampModified
    };
  }

  getFullName(): string {
    if (this.type === CustomerType.FISICA) {
      const name = this.businessName || this.name || '';
      const firstSurname = this.firstSurname || '';
      const secondSurname = this.secondSurname || '';
      return `${firstSurname} ${secondSurname}, ${name}`
        .replace(/\s+/g, ' ')
        .replace(/\s,/, ',')
        .replace(/^,\s*/, '')
        .trim();
    }
    return '';
  }

  getBusinessName(): string {
    if (this.type === CustomerType.JURIDICA && this.businessName) {
      return this.businessName;
    }
    return '';
  }

  getTradeName(): string {
    return this.tradeName || '';
  }

  getDisplayName(): string {
    if (this.type === CustomerType.FISICA) {
      return this.getFullName();
    }
    return this.businessName || this.tradeName || '';
  }
}

export interface CustomerRepository {
  findAll(companyId: string): Promise<Customer[]>;
  save(customer: Customer): Promise<Customer>;
  importFromExcel(companyId: string, fileContent: ArrayBuffer, filename: string): Promise<{ asyncJobId?: number }>;
  downloadTemplate(): Promise<string>;
}